import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'primeshine_dashboard_prefs';

const DEFAULT_PREFERENCES = {
  expandedSections: ['daily-operations'],
  viewMode: 'card', // 'card' or 'list'
  pinnedModules: [],
  sidebarCollapsed: false
};

/**
 * Custom hook for managing dashboard user preferences with localStorage persistence
 */
export const useDashboardPreferences = () => {
  // Initialize state from localStorage
  const [preferences, setPreferences] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all keys exist
        return { ...DEFAULT_PREFERENCES, ...parsed };
      }
      return DEFAULT_PREFERENCES;
    } catch (error) {
      console.warn('Failed to parse dashboard preferences from localStorage:', error);
      return DEFAULT_PREFERENCES;
    }
  });

  // Persist to localStorage whenever preferences change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save dashboard preferences to localStorage:', error);
    }
  }, [preferences]);

  /**
   * Toggle a section's expanded/collapsed state
   */
  const toggleSection = useCallback((sectionId) => {
    setPreferences(prev => {
      const isExpanded = prev.expandedSections.includes(sectionId);
      return {
        ...prev,
        expandedSections: isExpanded
          ? prev.expandedSections.filter(id => id !== sectionId)
          : [...prev.expandedSections, sectionId]
      };
    });
  }, []);

  /**
   * Expand a specific section (used by search auto-expand)
   */
  const expandSection = useCallback((sectionId) => {
    setPreferences(prev => {
      if (prev.expandedSections.includes(sectionId)) {
        return prev;
      }
      return {
        ...prev,
        expandedSections: [...prev.expandedSections, sectionId]
      };
    });
  }, []);

  /**
   * Expand multiple sections at once (for search results)
   */
  const expandSections = useCallback((sectionIds) => {
    setPreferences(prev => {
      const newExpanded = new Set([...prev.expandedSections, ...sectionIds]);
      return {
        ...prev,
        expandedSections: Array.from(newExpanded)
      };
    });
  }, []);

  /**
   * Set the view mode (card or list)
   */
  const setViewMode = useCallback((mode) => {
    if (mode === 'card' || mode === 'list') {
      setPreferences(prev => ({
        ...prev,
        viewMode: mode
      }));
    }
  }, []);

  /**
   * Toggle view mode between card and list
   */
  const toggleViewMode = useCallback(() => {
    setPreferences(prev => ({
      ...prev,
      viewMode: prev.viewMode === 'card' ? 'list' : 'card'
    }));
  }, []);

  /**
   * Toggle a module's pinned status
   */
  const togglePinnedModule = useCallback((moduleId) => {
    setPreferences(prev => {
      const isPinned = prev.pinnedModules.includes(moduleId);
      return {
        ...prev,
        pinnedModules: isPinned
          ? prev.pinnedModules.filter(id => id !== moduleId)
          : [...prev.pinnedModules, moduleId]
      };
    });
  }, []);

  /**
   * Check if a module is pinned
   */
  const isModulePinned = useCallback((moduleId) => {
    return preferences.pinnedModules.includes(moduleId);
  }, [preferences.pinnedModules]);

  /**
   * Check if a section is expanded
   */
  const isSectionExpanded = useCallback((sectionId) => {
    return preferences.expandedSections.includes(sectionId);
  }, [preferences.expandedSections]);

  /**
   * Reset all preferences to defaults
   */
  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
  }, []);

  return {
    preferences,
    toggleSection,
    expandSection,
    expandSections,
    setViewMode,
    toggleViewMode,
    togglePinnedModule,
    isModulePinned,
    isSectionExpanded,
    resetPreferences
  };
};

export default useDashboardPreferences;
